<script module lang="ts">
  export interface AppLayoutProps {
    main?: RegionComponent
    modal?: RegionComponent
    svelteModal?: RegionComponent
  }
</script>

<script lang="ts">
  import type { RegionComponent } from '#app/init_app_layout.svelte.ts'
  import type { ContextualizedError } from '#app/lib/error'
  import { logError } from '#app/lib/loggers'
  import { commands, vent } from '#app/radio'
  import ErrorLayout from '#components/error_layout.svelte'
  import { preventFormSubmit } from '#general/lib/prevent_form_submit'
  import { loggedIn } from '#user/lib/main_user'
  import DocumentHeaders from './document_headers.svelte'
  import FullScreenLoader from './full_screen_loader.svelte'
  import GlobalFlashMessage from './global_flash_message.svelte'
  import Modal from './modal.svelte'
  import TopBar from './top_bar.svelte'
  import type { Component } from 'svelte'

  const defaultMain = { component: FullScreenLoader, props: {} }

  let {
    main = defaultMain,
    modal,
    svelteModal,
  }: AppLayoutProps = $props()

  // Unfortunately, setting class:hasOverlay on <svelte:body> doesn't work
  // See https://github.com/sveltejs/svelte/issues/3105
  vent.on('overlay:shown', () => document.body.classList.add('hasOverlay'))
  vent.on('overlay:hidden', () => document.body.classList.remove('hasOverlay'))

  let showModal = $state(false)
  type ModalSize = 'large' | 'medium'
  let modalSize: ModalSize | undefined = $state()
  commands.setHandlers({
    'modal:open': (size?: ModalSize) => {
      showModal = true
      modalSize = size
    },
    'modal:close': () => {
      showModal = false
      modalSize = undefined
    },
    'show:loader': () => main = null,
  })

  $effect(() => {
    main ??= defaultMain
  })

  function handleError (err: unknown) {
    if (err instanceof Error) {
      (err as ContextualizedError).statusCode ??= 599
    }
    logError(err as ContextualizedError)
  }

  // svelte-ignore non_reactive_update
  let EmbeddedScanner: Component
  async function getEmbeddedScanner () {
    // Import dynamically to avoid importing its dependencies on every page load
    EmbeddedScanner ??= (await import('#inventory/components/add/embedded_scanner.svelte')).default
    return EmbeddedScanner
  }

  let showBarcodeScanner = $state(false)

  function onIsbnFound (isbn: string) {
    commands.execute('show:entity', isbn)
    showBarcodeScanner = false
  }
</script>

<DocumentHeaders />

<div class="top-bar">
  <TopBar bind:showBarcodeScanner />
</div>

<!--
  Note that once an error is catched, the boundary content will be removed,
  leaving the app in a non-recoverable state, so this is really a last resort
  See https://svelte.dev/docs/svelte/svelte-boundary
-->
<svelte:boundary onerror={handleError}>
  <main id="main">
    {#if main}
      <main.component {...main.props} />
    {/if}
  </main>

  {#if showModal && modal}
    <div class="modal-wrapper">
      <Modal size={modalSize} onCloseModal={() => showModal = false}>
        <modal.component {...modal.props} />
      </Modal>
    </div>
  {/if}

  {#if svelteModal}
    <svelteModal.component {...svelteModal.props} />
  {/if}

  {#snippet failed(error)}
    {@const { message, context } = error as ContextualizedError}
    <ErrorLayout {message} {context} offerToReload={true} />
  {/snippet}
</svelte:boundary>

<GlobalFlashMessage />

<!-- Display the barcode scanner overlay outside of the top bar, to not be affected by the call to displayAboveTopBar -->
{#if showBarcodeScanner}
  {#await getEmbeddedScanner() then}
    <EmbeddedScanner
      onClose={() => showBarcodeScanner = false}
      {onIsbnFound}
      showPostIsbnFoundOptions={loggedIn}
    />
  {/await}
{/if}

<svelte:body onsubmit={preventFormSubmit} />

<style lang="scss">
  @import "general/scss/utils";

  .top-bar{
    // Required to set a z-index
    position: relative;
    // Make the top bar appear above main
    z-index: 1;
  }

  :global(main){
    position: relative;
    z-index: 0;
  }
  /* Small screens */
  @media screen and (width < $small-screen){
    :global(main){
      // Allow scrollToElement to really get the top of the div
      // at the top of the screen, which would not be possible
      // if main was smaller than the screen
      min-height: calc(100vh - $topbar-height);
    }
    :global(main.active-connection-button){
      margin-block-end: $smallscreen-connection-buttons-height;
    }
  }

  @media screen and (height >= $top-bar-fixed-threshold){
    :global(main){
      // Prevent children margin-top to be collapsed with that margin-top
      // by setting a border, so that children margin-top start from that border
      // See https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Box_Model/Mastering_margin_collapsing
      border-block-start: 1px solid transparent;
      margin-block-start: calc($topbar-height - 1px);
    }
  }

  .modal-wrapper{
    z-index: 2;
  }

  :global(body.hasOverlay .top-bar){
    z-index: 0;
    background-color: red !important;
    // It would also be possible to disable body scroll to let the scroll monopoly to the overlay
    // but that would loose the scroll level when closing the overlay
    // overflow: hidden;
    // max-height: 100vh;
  }
</style>
